<template>
  <div>
    <div class="w-full">
      <div
        v-for="(
          { heading, hide, routerName, content, disabled, icon, primaryStatus }, index
        ) in sideNavComponents"
        :key="heading + index"
        class="overflow-hidden">
        <!--    start of new component    -->
        <div
          v-if="!hide"
          :class="[
            styles.base,
            {
              'cursor-pointer': isCollapsable || routerName,
              'bg-blue-5 rounded-sm': isPageActive(heading) && !content,
              'hover:bg-[#d7f5ff30] rounded-sm': !isPageActive(heading) || content,
              disabled: disabled,
            },
          ]"
          class="p-[20px] md:p-[10px] border-b border-blue-5 group"
          :data-test="heading + '-accordion'"
          @click="content ? toggleAccordion(heading) : redirectToTab(routerName ?? '')">
          <div class="flex justify-between">
            <div class="flex gap-[10px] items-center relative w-full">
              <!-- <ClipLoader-->
              <!-- v-if="isPageActive(heading)"-->
              <!-- class="bottom-0 right-0 absolute"-->
              <!-- :color="`var(&#45;&#45;primary)`"-->
              <!-- size="15px" />-->
              <component
                :is="icon"
                class="w-[14px] h-[14px]"
                :fill1="isPageActive(heading) && !content ? 'primary' : 'blue-1'"
                :fill2="isPageActive(heading) && !content ? 'primary' : 'blue-1'"
                hover1="primary"
                hover2="primary" />
              <div
                :class="[
                  styles.heading,
                  isPageActive(heading) && !content
                    ? 'text-primary '
                    : 'group-hover:text-primary font-medium!',
                ]">
                {{ heading }}
              </div>
            </div>
            <ArrowWide
              v-if="isCollapsable && content"
              :class="
                navsOpen[heading]
                  ? 'transition transform rotate-180'
                  : 'transition transform rotate-90'
              "
              :data-test="navsOpen[heading] ? `${heading}-arrow-180` : `${heading}-arrow-90`"
              color-class="text-blue-1" />
          </div>
          <Transition name="fast-slide">
            <div>
              <ClipLoader v-if="userLoading" class="text-primary" size="15px" />
              <Status
                v-else-if="primaryStatus && moduleOptions && !navsOpen[heading]"
                :module="parentModule(primaryStatus as ModuleType)"
                :state="parentStatus(primaryStatus)"
                status-style="outline"
                class="p-[0px] ml-[20px]" />
            </div>
          </Transition>
        </div>
        <!--    ./ end of new component    -->
        <Transition name="slide">
          <div
            v-show="navsOpen[heading]"
            :class="{ 'open border-b': navsOpen[heading] }"
            class="border-blue-5">
            <div v-for="item in content" :key="item.label + index">
              <div
                v-if="!item.hide"
                :class="[
                  navName === item.routerName ? 'bg-blue-5 text-primary' : '',
                  item.disabled
                    ? 'disabled pointer-events-none text-grey-3'
                    : 'hover:bg-[#d7f5ff50]',
                ]"
                class="cursor-pointer items-start ml-[20px] pl-[15px] p-[10px] rounded-l-sm"
                @click="redirectToTab(item.routerName, item?.routerParams || {})">
                <div class="flex gap-[10px]">
                  <component
                    :is="item.icon"
                    :fill1="styles.contentIconFill"
                    :fill2="styles.contentIconFill"
                    class="min-w-[16px] max-w-[16px] h-[16px]" />
                  <div
                    :class="[styles.content, { 'font-medium!': navName !== item.routerName }]"
                    :data-test="item.routerName">
                    {{ item.label }}
                  </div>
                </div>
                <ClipLoader v-if="userLoading" class="text-primary" size="15px" />
                <Status
                  v-else-if="moduleOptions && item?.routerName"
                  :module="item.routerName as ModuleType"
                  :state="childStatus(item.routerName as ModuleType)"
                  status-style="outline"
                  :data-test="item.routerName + '-status'"
                  class="p-0" />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import ArrowWide from '@/components/icons/ArrowWide.vue'
import { computed, nextTick, onBeforeMount, onMounted, ref } from 'vue'
import { SideNavAccordionContentType, SideNavAccordionsType } from '@/types/GlobalTypes.ts'
import { useNavBarStore } from '@/store/pinia/NavBarStore.ts'
import Status from '@/components/Status.vue'
import { ModuleStatusesType, ModuleType, useModuleStatusesStore, } from '@/store/pinia/ModuleStatusesStore.ts'
import { useDebounceFn } from '@vueuse/core'
import { useUserStore } from '@/store/pinia/UserStore.ts'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'
import { useSessionStore } from '@/store/pinia/SessionStore.ts'

const router = useRouter()

// NOTE: this component will be deprecated in the future and refactored into NavigationTabs

type AccordionStyle = {
  heading: string
  content: string
  contentIconFill: string
  base: string
}

interface Props {
  id: string
  sideNavAccordions: Array<SideNavAccordionsType>
  chevronColour?: string
  styles?: AccordionStyle
  isCollapsable?: boolean
}

interface SideNavOption {
  label: string
  value: string
}

const props = withDefaults(defineProps<Props>(), {
  borderColour: 'grey-2',
  chevronColour: '#888',
  isCollapsable: true,
  styles: () => {
    return {
      heading: 'text-16px',
      content: '',
      contentIconFill: 'primary',
      base: '',
    }
  },
})

const route = useRoute()
const navBarStore = useNavBarStore()
const moduleStatus = useModuleStatusesStore()
const userStore = useUserStore()
const whitelabel = computed(() => useSessionStore().getWhitelabelData)
const getUser = computed(() => userStore.getUser)
const userLoading = computed(() => userStore.isLoading)

const sideNavComponents = computed(() => props.sideNavAccordions)
const sideNavMobileSelectRef = ref<SideNavOption[]>([])
const navsOpen = computed(() => navBarStore.getNavsOpenState)

const moduleOptions = computed<ModuleStatusesType>(() => moduleStatus.getStatuses)

const navName = computed(() => route.name)
const emit = defineEmits(['click'])

onBeforeMount(async () => {
  if (!userLoading.value && !getUser.value.auth0id) {
    await userStore.fetchUser()
  }
  await nextTick()
})
onMounted(() => {
  sideNavComponents.value.forEach((sideNavAccordion) => {
    sideNavAccordion.content?.forEach((sideNavAccordionContent: SideNavAccordionContentType) => {
      sideNavMobileSelectRef.value.push({
        label: sideNavAccordionContent.label,
        value: sideNavAccordionContent.routerName,
      })
    })
    // hack to fix the issue of the accordion not opening when the page is refreshed
    if (isPageActive(sideNavAccordion.heading)) {
      navBarStore.setNavOpenState('Reporting', true)
    }
  })
})

const parentModule = (modules: Array<ModuleType> | ModuleType) => {
  if (Array.isArray(modules)) {
    return modules[0]
  }
  return modules
}

const freemiumEnabled = useLDFlag(FeatureList.ENABLE_FREEMIUM)
const showUpgradeState = computed(() => {
  return (
    freemiumEnabled &&
    getUser.value?.subscriptions?.length === 0 &&
    getUser.value?.active_subscription === false &&
    whitelabel.value.identifier === 'OTIVO'
  )
})

const parentStatus = (primaryStatus: string | Array<string>) => {
  if (primaryStatus === 'personalInsurance') {
    return moduleStatus.getHighestPriorityState('personalInsurance.single')
  }
  if (Array.isArray(primaryStatus) && showUpgradeState.value) {
    primaryStatus = primaryStatus.filter((module) => !moduleStatus.premiumModules.includes(module))
  }
  return moduleStatus.getHighestPriorityState(primaryStatus) || primaryStatus
}
const childStatus = (routerName: ModuleType) => {
  if (routerName === 'personalInsurance') {
    return moduleStatus.getHighestPriorityState('personalInsurance.single')
  }
  return moduleOptions?.value[routerName] ?? ''
}

const redirectToTab = useDebounceFn((routerName: string, params = {}) => {
  try {
    emit('click')
    router.push({ name: routerName, params })
  } catch (e) {
    console.error(e)
  }
}, 50)

const toggleAccordion = (accordionHeading: string) => {
  if (props.isCollapsable) {
    navBarStore.setNavOpenState(accordionHeading, !navsOpen.value[accordionHeading])
  }
}

const navLinks = computed(() => {
  const urls: Record<string, Array<string>> = {}
  sideNavComponents.value.forEach((sideNavAccordion) => {
    urls[sideNavAccordion.heading] = []
    if (sideNavAccordion.routerName) {
      urls[sideNavAccordion.heading].push(sideNavAccordion.routerName)
    }
    sideNavAccordion.content?.forEach((sideNavAccordionContent) => {
      urls[sideNavAccordion.heading].push(sideNavAccordionContent.routerName ?? '')
    })
  })
  return urls
})

const isPageActive = (heading: string) => {
  return navLinks.value[heading]?.includes((navName.value as string) || '')
}
</script>

<style scoped>
.slide-enter-active {
  transition: all 0.9s ease-in-out;
  max-height: 100vh;
}

.slide-leave-active {
  transition: all 0.9s ease-in-out;
  max-height: 100vh;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.fast-slide-enter-active {
  transition: all 0.3s ease-out;
  max-height: 20vh;
}

.fast-slide-leave-active {
  transition: all 0.3s ease-out;
  transition-delay: 0.2s;
  max-height: 20vh;
}

.fast-slide-enter-from,
.fast-slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

:deep(ul) {
  list-style: disc !important;
  margin-left: 10px !important;
}
</style>
